import React from 'react';
import { Bar } from 'react-chartjs-2';
import { useExecutiveCreditComparison } from '../../hooks/useExecutiveQueries';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartColors, getChartBackgroundColor, getChartJsOptions } from '../../utils/themeHelpers';
import { LoadingSpinner, ChartInfoButton } from '../ui';
import { CHART_GUIDES } from '../../constants/chartGuides';
import useChartGuide from '../../hooks/useChartGuide';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface CreditComparisonChartProps {
  branchId?: number;
  timeRange?: any;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

const CreditComparisonChart: React.FC<CreditComparisonChartProps> = ({
  branchId,
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  const { currentTheme } = useTheme();
  const chartColors = getChartColors(currentTheme);
  const { openGuide } = useChartGuide();
  
  // Fetch credit comparison data with timeRange
  const { data, isLoading, error } = useExecutiveCreditComparison(branchId, timeRange?.timeRange);
  
  const comparisonData = data || {
    labels: [],
    generatedCredits: [],
    usedCredits: [],
    revenue: [],
    invoices: []
  };
  
  // Chart options
  const barChartOptions = {
    ...getChartJsOptions(currentTheme),
    scales: {
      ...getChartJsOptions(currentTheme).scales,
      x: {
        ...getChartJsOptions(currentTheme).scales.x,
        ticks: {
          ...getChartJsOptions(currentTheme).scales?.x?.ticks,
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        ...getChartJsOptions(currentTheme).scales.y,
      }
    }
  };
  
  // Handle loading state
  if (showLoading && isLoading) {
    return (
      <div className="card-base card-padding">
        <div className="flex items-center justify-center min-h-[350px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری نمودار مقایسه…"
          />
        </div>
      </div>
    );
  }
  
  // Handle error
  if (error) {
    if (onError && error instanceof Error) {
      onError(error);
    }
    return null;
  }
  
  return (
    <div className="card-base card-padding animate-enter animate-delay-3">
      <ChartInfoButton 
        onClick={() => openGuide(CHART_GUIDES.creditProductionConsumption)}
        className="mb-4"
      />
      <div className="mb-6">
        <h3 className="title-card">مقایسه تولید و مصرف اعتبار در دوره زمانی</h3>
        <p className="text-text-secondary text-sm mt-1 text-center">تعداد اعتبارهای تولیدی و مصرفی در بازه زمانی مورد نظر</p>
      </div>
      
      <div style={{ height: compact ? 300 : 350, width: '100%' }}>
        {comparisonData.labels.length > 0 ? (
          <Bar
            data={{
              labels: comparisonData.labels,
              datasets: [
                {
                  label: "اعتبار تولیدی",
                  data: comparisonData.generatedCredits,
                  backgroundColor: getChartBackgroundColor(0, currentTheme, 0.8),
                  borderColor: chartColors[0],
                  borderWidth: 2,
                  borderRadius: 6,
                },
                {
                  label: "اعتبار مصرفی",
                  data: comparisonData.usedCredits,
                  backgroundColor: getChartBackgroundColor(2, currentTheme, 0.8),
                  borderColor: chartColors[2],
                  borderWidth: 2,
                  borderRadius: 6,
                }
              ]
            }}
            options={barChartOptions}
            aria-label="نمودار ستونی مقایسه اعتبار تولیدی و مصرفی"
          />
        ) : (
          <div className="flex items-center justify-center p-8 text-text-tertiary text-sm">
            <p>داده‌ای برای نمایش نمودار وجود ندارد</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditComparisonChart;